import { Alert } from '../../components/ui';
import { MfaCodeForm } from '../../components/auth/MfaCodeForm';
import { useLoginFlow, type LoginStage } from '../../hooks/useLoginFlow';

import { CredentialsForm } from './CredentialsForm';

const STAGE_COPY: Record<LoginStage, { title: string; description: string }> = {
  credentials: {
    title: 'Välkommen tillbaka',
    description: 'Använd ditt administratörskonto för att komma åt kontrollpanelen.',
  },
  mfa: {
    title: 'Bekräfta din identitet',
    description: 'Ange koden från din autentiseringsapp för att slutföra inloggningen.',
  },
  mfa_setup: {
    title: 'Sätt upp tvåfaktorsautentisering',
    description:
      'Skanna QR-koden med din autentiseringsapp och ange koden för att aktivera säkerhet.',
  },
};

const MFA_FORM_COPY = {
  mfa: {
    label: 'Engångskod',
    help: 'Öppna din autentiseringsapp och skriv in den sexsiffriga koden.',
    submit: 'Verifiera och logga in',
    submitting: 'Verifierar...',
    back: 'Tillbaka till inloggning',
  },
  mfa_setup: {
    label: 'Verifieringskod',
    help: 'Ange den sexsiffriga koden från din autentiseringsapp för att aktivera MFA.',
    submit: 'Aktivera MFA och logga in',
    submitting: 'Aktiverar...',
    back: 'Tillbaka till inloggning',
  },
};

export const LoginPage = () => {
  const {
    stage,
    pending,
    error,
    clearError,
    isAuthLoading,
    isLogging,
    isVerifying,
    submitCredentials,
    submitCode,
    backToCredentials,
  } = useLoginFlow();

  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-blue/10 to-brand-secondary-blue/20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue shadow-lg"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-blue/10 via-brand-beige-tint-1/50 to-brand-secondary-blue/15 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-brand-blue/5 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-brand-secondary-blue/5 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
      </div>

      <div className="mx-auto flex max-w-6xl flex-col items-center justify-center gap-10 lg:flex-row relative z-10">
        <div className="max-w-xl text-center lg:text-left">
          <h1 className="text-4xl font-extrabold bg-gradient-to-r from-brand-blue to-brand-secondary-blue bg-clip-text text-transparent sm:text-5xl font-display">
            Care of Carl Agent Admin
          </h1>
          <p className="mt-4 text-lg text-brand-blue/70">
            Logga in för att hantera samtal, dokument och säkerhetsinställningar för Care of Carl
            Agent Admin.
          </p>
        </div>

        <div className="w-full max-w-md">
          <div className="rounded-2xl bg-gradient-to-br from-basic-white/80 to-brand-beige-tint-1/60 backdrop-blur-lg p-8 shadow-2xl border border-brand-blue/10">
            <div className="mb-8 flex flex-col items-center text-center">
              <div className="h-16 w-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-brand-blue to-brand-secondary-blue shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-300">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
              <h2 className="mt-4 text-2xl font-semibold bg-gradient-to-r from-brand-blue to-brand-secondary-blue bg-clip-text text-transparent font-display">
                {STAGE_COPY[stage].title}
              </h2>
              <p className="mt-2 text-sm text-brand-blue/70">{STAGE_COPY[stage].description}</p>
            </div>

            {error && (
              <div className="mb-6">
                <Alert type="error" message={error} />
              </div>
            )}

            {!pending ? (
              <CredentialsForm
                isSubmitting={isLogging}
                onSubmit={submitCredentials}
                onDirty={clearError}
              />
            ) : (
              <div className="space-y-6">
                {pending.stage === 'mfa_setup' && (
                  <div className="text-center">
                    <p className="mb-4 text-sm text-brand-blue/70">
                      Skanna QR-koden med din autentiseringsapp:
                    </p>
                    <div className="mx-auto w-48 h-48 bg-white p-4 rounded-lg">
                      <img
                        src={pending.setup.qr_code}
                        alt="QR Code för tvåfaktorsautentisering"
                        className="w-full h-full"
                      />
                    </div>
                    <div className="mt-4 p-3 bg-brand-blue/5 rounded-lg">
                      <p className="text-xs text-brand-blue/70 mb-2">Manual nyckel:</p>
                      <code className="text-xs font-mono text-brand-blue break-all">
                        {pending.setup.secret}
                      </code>
                    </div>
                  </div>
                )}

                <MfaCodeForm
                  inputId={pending.stage === 'mfa_setup' ? 'mfa-setup' : 'mfa'}
                  copy={MFA_FORM_COPY[pending.stage]}
                  isSubmitting={isVerifying}
                  onSubmit={submitCode}
                  onBack={backToCredentials}
                  onDirty={clearError}
                />
              </div>
            )}

            <p className="mt-8 text-center text-xs text-slate-600">
              Har du problem att logga in? Kontakta webb på Care of Carl.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
